import React, { useState, useEffect, useMemo, useTransition, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet";
import { NavLink } from "react-router-dom";
import "./PhotoGallery2026.css";

// Helper to generate image objects dynamically
const generateImages = (category, count) => {
  // Use the exact category name for the folder path to match capitalized/hyphenated folders
  const folderName = category.replace(/\s+/g, '');
  return Array.from({ length: count }, (_, i) => ({
    id: `${folderName.toLowerCase()}-${i + 1}`,
    src: `/assets/images/gallery/year-2026/${folderName}/${i + 1}.webp`,
    description: `${category.replace(/-/g, ' ')} moment at MSB Association 2026`,
    category: category
  }));
};

const categories = [
  "All",
  "Venue",
  "Registration",
  "Discussion-panel",
  "Keynote-speaker",
  "Networking",
  "Food",
  "Goodies",
  "our-team",
  "Clear-Junction-Team",
  "Board-memebers",
  "Other"
];

const PhotoGallery2026 = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(12);
  const [isPending, startTransition] = useTransition();

  const titleText = "Photo Gallery 2026";
  const letterVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: (index) => ({
      y: 0,
      opacity: 1,
      transition: { delay: index * 0.05, duration: 0.4, ease: "easeInOut" },
    }),
  };

  const tabContentVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.98 },
    visible: (i) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: (i % 12) * 0.05, duration: 0.3 },
    }),
  };

  // Configuration for image counts
  const imageConfig = {
    "Venue": 17,
    "Registration": 11,
    "Discussion-panel": 4,
    "Keynote-speaker": 3,
    "Networking": 24,
    "Food": 17,
    "Goodies": 3,
    "our-team": 8,
    "Clear-Junction-Team": 7,
    "Board-memebers": 5,
    "Other": 10
  };

  // Memoize all photos generation
  const allPhotos = useMemo(() => {
    return Object.entries(imageConfig).flatMap(([cat, count]) => generateImages(cat, count));
  }, []);

  // Memoize filtered photos logic
  const filteredPhotos = useMemo(() => {
    return activeTab === "All" 
      ? allPhotos 
      : allPhotos.filter(photo => photo.category === activeTab);
  }, [activeTab, allPhotos]);

  // Reset visible count when switching tabs
  useEffect(() => {
    startTransition(() => {
      setVisibleCount(12);
    });
  }, [activeTab]);

  const displayedPhotos = filteredPhotos.slice(0, visibleCount);

  const openModal = useCallback((index) => {
    setSelectedImageIndex(index);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const nextImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev + 1) % filteredPhotos.length);
  }, [filteredPhotos.length]);

  const prevImage = useCallback(() => {
    setSelectedImageIndex((prev) => (prev - 1 + filteredPhotos.length) % filteredPhotos.length);
  }, [filteredPhotos.length]);

  const handleShowMore = () => {
    startTransition(() => {
      setVisibleCount((prev) => prev + 12);
    });
  };

  // Handle key navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen) return;
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, nextImage, prevImage, closeModal]);

  return (
    <div className={`photo_gallery_2026_wrapper ${isPending ? "rendering" : ""}`}>
      <Helmet>
        <title>Photo Gallery 2026 | MSB Association UK</title>
      </Helmet>

      {/* Hero Section / Banner */}
      <section className="simple_page_header overflow-hidden">
        <div className="container">
          <motion.h1 
            className="page_title_simple"
            initial="hidden"
            animate="visible"
            style={{ display: "flex", justifyContent: "center" }}
          >
            {titleText.split("").map((char, index) => (
              <motion.span
                key={index}
                custom={index}
                variants={letterVariants}
                style={{ display: "inline-block", whiteSpace: char === " " ? "pre" : "normal" }}
              >
                {char}
              </motion.span>
            ))}
          </motion.h1>
          <ul className="breadcrumb_nav unordered_list justify-content-center">
            <li><NavLink to="/">Home</NavLink></li>
            
            <li className="active"><a href="#!">2026 Photos</a></li>
          </ul>
        </div>
      </section>

      {/* Main Gallery Section */}
      <section className="gallery_section section_space">
        <div className="container">
          {/* Tabs Navigation - Intelligent Responsive Layout */}
          <div className="gallery_tabs_container mb-5 text-center">
            <div className="merged_tabs_pill">
              {categories.map((cat, index) => {
                const isSecondRowItem = index >= 6;
                return (
                  <React.Fragment key={cat}>
                    {/* On Desktop, force a line break before 'Food' */}
                    {index === 6 && <div className="d-none d-md-block w-100" style={{ height: '0' }}></div>}
                    <button
                      className={`gallery_tab_btn ${activeTab === cat ? "active" : ""}`}
                      onClick={() => {
                        startTransition(() => {
                          setActiveTab(cat);
                        });
                      }}
                    >
                      {(() => {
                        let label = cat.replace(/-/g, ' ');
                        if (label.toLowerCase() === "board memebers") label = "Board members";
                        return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
                      })()}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Photos Grid Container with Wait Mode Transition */}
          <AnimatePresence mode="wait">
            <motion.div 
              key={activeTab}
              variants={tabContentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="row justify-content-center"
            >
              {displayedPhotos.map((photo, index) => (
                <motion.div
                  key={photo.id}
                  custom={index}
                  variants={imageVariants}
                  initial="hidden"
                  animate="visible"
                  className="col-lg-3 col-md-4 col-sm-6 mb-4"
                  onClick={() => openModal(index)}
                >
                  <div className={`gallery_item ${['Other', 'Board-memebers'].includes(photo.category) ? 'portrait_mode' : ''}`}>
                    <div className="img_wrap">
                      <img 
                        src={photo.src} 
                        alt={photo.description} 
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = "https://placehold.co/600x400/f1f1f1/777777?text=Image+Coming+Soon";
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>

          {visibleCount < filteredPhotos.length && (
            <div className="show_more_container text-center mt-5">
              <button className="btn_show_more" onClick={handleShowMore}>
                Show More <i className="fa-solid fa-chevron-down ms-2"></i>
              </button>
            </div>
          )}

          {filteredPhotos.length === 0 && (
            <div className="text-center py-5">
              <p className="no_photos_text">No photos available in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {modalOpen && (
          <motion.div 
            className="gallery_lightbox_overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div 
              className="lightbox_content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="lightbox_main">
                <div className="img_container">
                  <div className="img_relative_wrap">
                    <button className="nav_btn prev" onClick={prevImage}>
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    
                    <img src={filteredPhotos[selectedImageIndex].src} alt="Gallery Full" />
                    
                    <button className="nav_btn next" onClick={nextImage}>
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>

                    <button className="close_btn" onClick={closeModal}>
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                  <p className="img_caption">{filteredPhotos[selectedImageIndex].description}</p>
                </div>
              </div>

              <div className="lightbox_footer">
                <span className="count_text">
                  {selectedImageIndex + 1} / {filteredPhotos.length}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoGallery2026;
